// Thin fetch wrapper. Distinguishes "the server said no" (ApiError) from
// "we could not reach the server" (NetworkError), so the UI can say which.

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: any) {
    super(message);
  }
  get fields(): { path: string; message: string }[] {
    return this.details?.fields ?? [];
  }
}

export class NetworkError extends Error {
  constructor() {
    super('Could not reach the server. Check the connection. Nothing was saved by this attempt.');
  }
}

type Options = { method?: string; body?: unknown; form?: FormData; signal?: AbortSignal };

export async function api<T = any>(path: string, opts: Options = {}): Promise<T> {
  const headers: Record<string, string> = { 'X-Requested-With': 'mal-app' };
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method: opts.method ?? (body ? 'POST' : 'GET'), headers, body, credentials: 'same-origin', signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new NetworkError();
  }
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event('mal:unauthorized'));
    const err = data?.error;
    if (!err && res.status >= 500) throw new NetworkError();
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `Request failed (${res.status})`, err?.details);
  }
  return data as T;
}

/** Same as api() but returns {status, body} for endpoints where 404/409 carry useful data. */
export async function apiRaw(path: string): Promise<{ status: number; body: any }> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { headers: { 'X-Requested-With': 'mal-app' }, credentials: 'same-origin' });
  } catch {
    throw new NetworkError();
  }
  if (res.status === 401) window.dispatchEvent(new Event('mal:unauthorized'));
  return { status: res.status, body: await res.json().catch(() => null) };
}

/** POST that returns a file (e.g. a label PDF). Errors are reported like api(). */
export async function apiFile(path: string, body: unknown): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'mal-app', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch {
    throw new NetworkError();
  }
  if (!res.ok) {
    const err = (await res.json().catch(() => null))?.error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `Request failed (${res.status})`, err?.details);
  }
  return res.blob();
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const photoUrl =(id: string, variant: 'thumb' | 'full' = 'thumb') => `/api/photos/${id}/${variant}`;

export const enc = encodeURIComponent;

export interface Product {
  id: string;
  itemNumber: string;
  source: 'csv_import' | 'manual' | 'web';
  webPage?: { id: string; url: string; sourceName: string; fetchedAt: string } | null;
  importId: string | null;
  importRowNumber: number | null;
  importFilename?: string | null;
  manifestTitle: string | null;
  manifestPrice: string | null;
  manifestCurrency: string | null;
  manifestSellPrice?: string | null;
  importSheet?: string | null;
  sourceItemId?: string | null;
  /** As imported (master list or Costco page), never edited. */
  category?: string | null;
  subcategory?: string | null;
  /** Clean category worked out from those and the Costco page: Clothing or Hard Goods, and the real subcategory. */
  mainCategory?: MainCategory | null;
  realSubcategory?: string | null;
  taxCategory?: string | null;
  listingTitle: string | null;
  referencePrice: string | null;
  referenceCurrency: string | null;
  description: string | null;
  listingTitleSourcePageId?: string | null;
  descriptionSourcePageId?: string | null;
  hasWebData?: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdByName?: string;
  updatedByName?: string | null;
  barcodes?: { id: string; barcode: string; source: 'employee' | 'import'; createdAt: string; createdByName: string }[];
  lotCount?: number;
}

export interface Location {
  id: string;
  code: string;
  warehouse: string | null;
  aisle: string | null;
  rack: string | null;
  shelf: string | null;
  bin: string | null;
  notes?: string | null;
  active: boolean;
  lotCount?: number;
}

export interface Condition {
  id: string;
  label: string;
  sortOrder: number;
  active: boolean;
}

export interface Photo {
  id: string;
  isPrimary: boolean;
  sortOrder: number;
  /** 'catalogue' = the Costco website photo for this item. */
  source?: 'staff' | 'catalogue';
}

/** Information read from a Costco website page for one item number. */
export interface WebPage {
  id: string;
  source: string;
  sourceName: string;
  url: string;
  sku: string;
  name: string | null;
  brand: string | null;
  model: string | null;
  summary: string | null;
  features: string[] | null;
  specifications: [string, string][] | null;
  breadcrumbs: string[] | null;
  price: string | null;
  currency: string | null;
  fetchedAt: string;
  images: { id: string }[];
  /** Summary + features as one text: what a lot's Costco description is pre-filled with. */
  description: string | null;
}

export const webImageUrl = (id: string, variant: 'thumb' | 'full' = 'thumb') => `/api/web-images/${id}/${variant}`;

export interface Lot {
  id: string;
  lotNumber: string;
  status: 'available' | 'void';
  voidReason: string | null;
  /** Null for a mixed/parts lot. */
  productId: string | null;
  productItemNumber: string | null;
  kind: 'product' | 'mixed';
  completeness: 'complete' | 'incomplete';
  piecesPresent: number | null;
  piecesTotal: number | null;
  overHalfConfirmed: boolean;
  missingParts: string | null;
  /** Mixed lots: the pieces, one per line. */
  contents: string | null;
  mainCategory: MainCategory | null;
  realSubcategory: string | null;
  /** What we paid: from the manifest, or estimated as retail x costPercent. */
  cost: string | null;
  costCurrency: string | null;
  costSource: 'estimated' | 'manifest' | 'none';
  costPercent: number | null;
  title: string | null;
  titleSource: string;
  titleSourceUrl?: string | null;
  referencePrice: string | null;
  referenceCurrency: string | null;
  referencePriceSource: string;
  startingBid: string | null;
  reservePrice: string | null;
  bidCurrency: string | null;
  conditionId: string | null;
  conditionLabel: string | null;
  colour: string | null;
  sizeModel: string | null;
  details: string | null;
  costcoDescription: string | null;
  costcoDescriptionSource: 'costco_page' | 'product_description' | 'employee' | 'none';
  costcoDescriptionUrl: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdByName: string;
  updatedByName: string | null;
  location: Location | null;
  bin: { id: string; code: string } | null;
  product: { id: string; itemNumber: string; manifestTitle: string | null; listingTitle: string | null } | null;
  photos: Photo[];
  locationHistory: { movedAt: string; fromCode: string | null; toCode: string | null; movedByName: string; note: string | null; binCode: string | null }[];
}

export interface Bin {
  id: string;
  code: string;
  locationId: string | null;
  locationCode: string | null;
  notes: string | null;
  active: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  lotCount: number;
  printCount: number;
}

export interface BinDetail extends Bin {
  createdByName: string;
  lots: LotSummary[];
  locationHistory: { movedAt: string; fromCode: string | null; toCode: string | null; lotCount: number; movedByName: string; note: string | null }[];
}

/** Bin labels are "BIN" + digits; same rule as the server. */
export const isBinCode = (code: string) => /^BIN\d{5,}$/i.test(code.trim());

export interface LotSummary {
  id: string;
  lotNumber: string;
  status: string;
  title: string | null;
  productId: string | null;
  productItemNumber: string | null;
  kind: 'product' | 'mixed';
  completeness: 'complete' | 'incomplete';
  piecesPresent: number | null;
  piecesTotal: number | null;
  conditionLabel: string | null;
  colour: string | null;
  locationId: string | null;
  locationCode: string | null;
  binId: string | null;
  binCode: string | null;
  createdAt: string;
  primaryPhotoId: string | null;
}

export type MainCategory = 'clothing' | 'hard_goods';
export const MAIN_CATEGORY_LABEL: Record<MainCategory, string> = { clothing: 'Clothing', hard_goods: 'Hard Goods' };
/** "Hard Goods › Housewares", or null when there's no category. */
export const categoryText = (main: MainCategory | null | undefined, sub: string | null | undefined) =>
  main ? [MAIN_CATEGORY_LABEL[main], sub].filter(Boolean).join(' › ') : null;

/** Estimated cost: retail x the category's %, to the cent (same rule as the server). Null without both. */
export function estimatedCost(retail: string | null | undefined, category: MainCategory | null | undefined, percents: Record<MainCategory, number> | undefined) {
  const r = Number(retail);
  if (!retail?.trim() || !Number.isFinite(r) || !category || !percents) return null;
  return { cost: (Math.round((Math.round(r * 100) * percents[category]) / 100) / 100).toFixed(2), percent: percents[category] };
}

export const productTitle =(p: Pick<Product, 'listingTitle' | 'manifestTitle'>) => p.listingTitle ?? p.manifestTitle ?? null;
